import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import api from "../utils/api";

/* ── Utilities ──────────────────────────────────────────────────────────────── */
function initials(str = "") {
  const parts = str.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return str.slice(0, 2).toUpperCase();
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}
function fmtDateChat(d) {
  const dt = new Date(d),
    now = new Date(),
    yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (dt.toDateString() === now.toDateString()) return "Today";
  if (dt.toDateString() === yest.toDateString()) return "Yesterday";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const STATUS_CONFIG = {
  pending: { label: "Pending", cls: "badge-orange" },
  reviewed: { label: "Reviewed", cls: "badge-blue" },
  accepted: { label: "Accepted", cls: "badge-green" },
  rejected: { label: "Rejected", cls: "badge-red" },
};

const INTERVIEWER_STATUSES = ["pending", "reviewed", "rejected"];

/* ── Interview message templates ─────────────────────────────────────────────── */
const INTERVIEW_TEMPLATES = [
  {
    label: "👋 Greeting",
    text: "Hi! Welcome to your interview with Quantum Logics. We're glad to have you here. Let's get started!",
  },
  {
    label: "📋 Tasks intro",
    text: "So, there will be tasks based on learning. You will be assigned tasks that you have to complete. There is no fixed timing, but there will be a deadline by which you must finish them. Are you interested?",
  },
  {
    label: "✅ Wrap-up",
    text: "Thank you for your time. We will contact you via email if you are accepted. If you have any further questions, please feel free to ask.",
  },
  {
    label: "📎 Next steps",
    text: "Please check your email for further instructions and details regarding the next steps.",
  },
  {
    label: "🕐 Schedule",
    text: "Could you let us know your availability for a technical discussion this week?",
  },
];

/* ── Styles ─────────────────────────────────────────────────────────────────── */
const STYLES = `
  @keyframes iw-toast-in { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  @keyframes iw-spin { to{transform:rotate(360deg)} }

  .iw-toast {
    position:fixed;bottom:24px;right:24px;z-index:9999;
    padding:11px 18px;border-radius:10px;font-size:.875rem;font-weight:600;
    background:var(--surface);border:1px solid var(--line-2);
    color:var(--text-1);box-shadow:0 8px 32px rgba(0,0,0,.35);
    animation:iw-toast-in .22s cubic-bezier(.16,1,.3,1);
    max-width:340px;pointer-events:none;
  }
  .iw-toast.success{border-color:rgba(34,197,94,.35);color:var(--green);}
  .iw-toast.error{border-color:rgba(239,68,68,.35);color:var(--red);}

  .iw-spinner{width:13px;height:13px;border-radius:50%;border:2px solid rgba(255,255,255,.25);border-top-color:#fff;animation:iw-spin .6s linear infinite;display:inline-block;}

  .iw-dash{display:flex;min-height:100vh;background:var(--bg);color:var(--text-1);font-family:var(--body);}

  .iw-sidebar{
    width:230px;flex-shrink:0;display:flex;flex-direction:column;
    border-right:1px solid var(--line);background:var(--bg-1);
    position:sticky;top:0;height:100vh;overflow-y:auto;
    transition:transform .25s cubic-bezier(.16,1,.3,1);z-index:50;
  }
  @media(max-width:768px){
    .iw-sidebar{position:fixed;inset-block:0;left:0;transform:translateX(-100%);}
    .iw-sidebar.open{transform:translateX(0);box-shadow:4px 0 32px rgba(0,0,0,.4);}
    .iw-main{margin-left:0!important;}
  }
  .iw-sidebar-top{display:flex;align-items:center;gap:10px;padding:18px 16px 14px;border-bottom:1px solid var(--line);}
  .iw-sidebar-brand{font-family:var(--display);font-weight:800;font-size:.95rem;color:var(--text-1);}
  .iw-sidebar-role-badge{
    display:inline-flex;align-items:center;gap:5px;
    padding:2px 9px;border-radius:100px;font-size:.68rem;font-weight:700;
    background:rgba(124,58,237,.15);border:1px solid rgba(124,58,237,.3);
    color:#a78bfa;margin:8px 16px 4px;letter-spacing:.02em;
  }
  .iw-sidebar-label{font-size:.68rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-3);padding:12px 16px 5px;}
  .iw-sidebar-btn{
    display:flex;align-items:center;gap:10px;padding:9px 16px;
    border:none;width:100%;background:transparent;font-family:var(--body);
    font-size:.875rem;color:var(--text-2);cursor:pointer;text-align:left;
    transition:background .12s,color .12s;border-left:2px solid transparent;
  }
  .iw-sidebar-btn:hover{background:rgba(255,255,255,.04);color:var(--text-1);}
  .iw-sidebar-btn.active{background:rgba(79,142,247,.1);color:var(--blue);border-left-color:var(--blue);}
  .iw-sidebar-badge{margin-left:auto;min-width:18px;height:18px;border-radius:100px;padding:0 5px;background:#ef4444;color:#fff;font-size:.6rem;font-weight:800;display:flex;align-items:center;justify-content:center;}
  .iw-sidebar-footer{margin-top:auto;padding:12px 16px;border-top:1px solid var(--line);}
  .iw-sidebar-user-name{font-size:.82rem;font-weight:600;color:var(--text-1);}
  .iw-sidebar-user-email{font-size:.73rem;color:var(--text-3);margin-top:2px;word-break:break-all;}
  .iw-sidebar-logout{margin-top:8px;width:100%;display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;border:1px solid var(--line-2);background:transparent;color:var(--text-2);font-family:var(--body);font-size:.82rem;cursor:pointer;transition:all .13s;}
  .iw-sidebar-logout:hover{background:rgba(239,68,68,.08);color:var(--red);border-color:rgba(239,68,68,.3);}
  .iw-sidebar-backdrop{position:fixed;inset:0;z-index:49;background:rgba(0,0,0,.5);}

  .iw-main{flex:1;min-width:0;display:flex;flex-direction:column;}

  .iw-topbar{
    display:flex;align-items:center;gap:10px;
    padding:12px 20px;border-bottom:1px solid var(--line);
    background:var(--bg-1);position:sticky;top:0;z-index:20;min-height:56px;
  }
  .iw-topbar-toggle{width:36px;height:36px;border-radius:9px;border:1px solid var(--line-2);background:transparent;color:var(--text-2);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .13s;}
  .iw-topbar-toggle:hover{background:var(--surface);color:var(--text-1);}
  @media(min-width:769px){.iw-topbar-toggle{display:none;}}
  .iw-topbar-title{font-family:var(--display);font-weight:800;font-size:1.05rem;color:var(--text-1);letter-spacing:-.02em;}
  .iw-topbar-right{margin-left:auto;display:flex;align-items:center;gap:8px;}

  .iw-content{flex:1;padding:24px 28px;overflow-y:auto;}
  @media(max-width:640px){.iw-content{padding:16px 14px;}}

  .iw-page-header{margin-bottom:1.5rem;}
  .iw-page-header h1{font-family:var(--display);font-size:1.5rem;font-weight:800;color:var(--text-1);letter-spacing:-.025em;margin-bottom:.25rem;}
  .iw-page-header p{font-size:.875rem;color:var(--text-2);line-height:1.55;}

  .iw-card{background:var(--surface);border:1px solid var(--line-2);border-radius:14px;padding:1.25rem;margin-bottom:1rem;}

  .iw-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:.875rem;margin-bottom:1.5rem;}
  .iw-stat-card{background:var(--surface);border:1px solid var(--line-2);border-radius:12px;padding:1.1rem 1.25rem;}
  .iw-stat-label{font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--text-3);margin-bottom:.375rem;}
  .iw-stat-num{font-family:var(--display);font-size:1.75rem;font-weight:800;color:var(--text-1);letter-spacing:-.03em;}
  .iw-stat-sub{font-size:.73rem;color:var(--text-3);margin-top:.25rem;}

  .iw-filter-bar{display:flex;align-items:center;gap:.625rem;flex-wrap:wrap;padding:.75rem 1rem;background:var(--bg-1);border:1px solid var(--line);border-radius:11px;margin-bottom:1rem;}
  .iw-search-wrap{position:relative;flex:1;min-width:160px;}
  .iw-search-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--text-3);pointer-events:none;}
  .iw-search{width:100%;padding:7px 10px 7px 32px;background:var(--surface);border:1px solid var(--line-2);border-radius:8px;font-family:var(--body);font-size:.84rem;color:var(--text-1);outline:none;box-sizing:border-box;transition:border-color .14s;}
  .iw-search:focus{border-color:var(--blue);}
  .iw-search::placeholder{color:var(--text-3);}
  .iw-pill{padding:4px 11px;border-radius:100px;border:1px solid var(--line-2);background:transparent;font-family:var(--body);font-size:.75rem;font-weight:600;color:var(--text-2);cursor:pointer;transition:all .12s;white-space:nowrap;}
  .iw-pill:hover{background:var(--surface);color:var(--text-1);}
  .iw-pill.active{background:rgba(79,142,247,.12);border-color:rgba(79,142,247,.4);color:var(--blue);}
  .iw-pill.active-pending{background:rgba(245,158,11,.12);border-color:rgba(245,158,11,.4);color:var(--amber);}
  .iw-pill.active-reviewed{background:rgba(79,142,247,.12);border-color:rgba(79,142,247,.4);color:var(--blue);}
  .iw-pill.active-rejected{background:rgba(239,68,68,.12);border-color:rgba(239,68,68,.4);color:var(--red);}

  .iw-table-wrap{overflow-x:auto;}
  .iw-table-wrap table{width:100%;border-collapse:collapse;font-size:.84rem;}
  .iw-table-wrap th{padding:9px 12px;text-align:left;font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--text-3);border-bottom:1px solid var(--line);white-space:nowrap;}
  .iw-table-wrap td{padding:10px 12px;border-bottom:1px solid rgba(255,255,255,.04);vertical-align:middle;}
  .iw-table-wrap tr:last-child td{border-bottom:none;}
  .iw-table-wrap tr:hover td{background:rgba(255,255,255,.02);}
  .iw-td-user{display:flex;align-items:center;gap:9px;}
  .iw-td-avatar{width:32px;height:32px;border-radius:50%;flex-shrink:0;background:linear-gradient(135deg,var(--blue),#818cf8);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:800;color:#fff;}
  .iw-td-name{font-size:.84rem;font-weight:600;color:var(--text-1);}
  .iw-td-email{font-size:.73rem;color:var(--text-3);margin-top:1px;}
  .iw-td-secondary{color:var(--text-2);font-size:.82rem;}

  .iw-status-select{
    padding:4px 8px;border-radius:7px;font-family:var(--body);font-size:.78rem;
    font-weight:600;background:var(--surface);border:1px solid var(--line-2);
    color:var(--text-1);cursor:pointer;outline:none;
  }
  .iw-status-select:focus{border-color:var(--blue);}

  .iw-card-list{display:none;}
  @media(max-width:700px){
    .iw-table-wrap table{display:none;}
    .iw-card-list{display:flex;flex-direction:column;gap:.625rem;}
  }
  .iw-list-item{display:flex;align-items:center;gap:10px;padding:.875rem 1rem;background:var(--bg-1);border:1px solid var(--line-2);border-radius:10px;}
  .iw-list-avatar{width:40px;height:40px;border-radius:50%;flex-shrink:0;background:linear-gradient(135deg,var(--blue),#818cf8);display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:800;color:#fff;}
  .iw-list-info{flex:1;min-width:0;}
  .iw-list-name{font-weight:600;color:var(--text-1);font-size:.875rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .iw-list-sub{font-size:.73rem;color:var(--text-3);margin-top:2px;}
  .iw-list-right{display:flex;flex-direction:column;align-items:flex-end;gap:5px;flex-shrink:0;}

  .iw-chat-root{display:flex;height:calc(100vh - 57px);overflow:hidden;background:var(--bg);}
  .iw-chat-sidebar{width:270px;flex-shrink:0;display:flex;flex-direction:column;border-right:1px solid var(--line);background:var(--bg-1);}
  @media(max-width:700px){
    .iw-chat-sidebar{position:absolute;inset-block:0;left:0;z-index:20;width:82vw;max-width:300px;transform:translateX(-100%);transition:transform .22s cubic-bezier(.16,1,.3,1);box-shadow:4px 0 32px rgba(0,0,0,.4);}
    .iw-chat-sidebar.open{transform:translateX(0);}
    .iw-chat-area{width:100%!important;}
  }
  .iw-chat-sb-hdr{padding:12px 14px;border-bottom:1px solid var(--line);display:flex;align-items:center;justify-content:space-between;}
  .iw-chat-sb-title{font-family:var(--display);font-weight:800;font-size:.95rem;color:var(--text-1);}
  .iw-chat-conv-list{flex:1;overflow-y:auto;}
  .iw-chat-conv-item{display:flex;align-items:center;gap:9px;padding:10px 13px;cursor:pointer;transition:background .11s;border-left:2px solid transparent;}
  .iw-chat-conv-item:hover{background:rgba(255,255,255,.03);}
  .iw-chat-conv-item.active{background:rgba(79,142,247,.09);border-left-color:var(--blue);}
  .iw-chat-conv-av{width:36px;height:36px;border-radius:50%;flex-shrink:0;background:linear-gradient(135deg,#4f8ef7,#7c3aed);display:flex;align-items:center;justify-content:center;font-size:.66rem;font-weight:800;color:#fff;position:relative;}
  .iw-chat-conv-badge{position:absolute;top:-2px;right:-2px;width:14px;height:14px;border-radius:50%;background:#ef4444;border:2px solid var(--bg-1);display:flex;align-items:center;justify-content:center;font-size:.52rem;font-weight:800;color:#fff;}
  .iw-chat-conv-name{font-size:.83rem;font-weight:600;color:var(--text-1);}
  .iw-chat-conv-preview{font-size:.73rem;color:var(--text-3);margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px;}
  .iw-chat-area{flex:1;min-width:0;display:flex;flex-direction:column;position:relative;}
  .iw-chat-hdr{display:flex;align-items:center;gap:10px;padding:11px 16px;border-bottom:1px solid var(--line);background:var(--bg-1);flex-shrink:0;}
  .iw-chat-hdr-av{width:36px;height:36px;border-radius:50%;background:linear-gradient(135deg,#4f8ef7,#7c3aed);display:flex;align-items:center;justify-content:center;font-size:.66rem;font-weight:800;color:#fff;flex-shrink:0;}
  .iw-chat-hdr-name{font-size:.9rem;font-weight:700;color:var(--text-1);}
  .iw-chat-hdr-email{font-size:.72rem;color:var(--text-3);}
  .iw-chat-msgs{flex:1;overflow-y:auto;padding:16px 18px;display:flex;flex-direction:column;gap:8px;}
  .iw-chat-date-sep{text-align:center;font-size:.68rem;font-weight:700;color:var(--text-3);letter-spacing:.05em;text-transform:uppercase;padding:6px 0;}
  .iw-chat-msg{display:flex;flex-direction:column;max-width:70%;}
  .iw-chat-msg.me{align-self:flex-end;align-items:flex-end;}
  .iw-chat-msg.them{align-self:flex-start;align-items:flex-start;}
  .iw-chat-sender{font-size:.63rem;font-weight:700;color:var(--text-3);margin-bottom:3px;}
  .iw-chat-bubble{padding:9px 14px;border-radius:14px;font-size:.875rem;line-height:1.55;word-break:break-word;white-space:pre-wrap;}
  .iw-chat-msg.me .iw-chat-bubble{background:linear-gradient(135deg,#4f8ef7,#6366f1);color:#fff;border-bottom-right-radius:4px;}
  .iw-chat-msg.them .iw-chat-bubble{background:var(--surface);color:var(--text-1);border:1px solid var(--line-2);border-bottom-left-radius:4px;}
  .iw-chat-time{font-size:.62rem;color:var(--text-3);margin-top:3px;}
  .iw-chat-input-row{display:flex;align-items:flex-end;gap:10px;padding:11px 16px 14px;border-top:1px solid var(--line);background:rgba(0,0,0,.1);flex-shrink:0;}
  .iw-chat-textarea{flex:1;resize:none;background:var(--surface);border:1px solid var(--line-2);border-radius:10px;padding:9px 13px;font-family:var(--body);font-size:.875rem;color:var(--text-1);outline:none;min-height:40px;max-height:120px;line-height:1.5;transition:border-color .14s;}
  .iw-chat-textarea:focus{border-color:var(--blue);}
  .iw-chat-send-btn{width:40px;height:40px;flex-shrink:0;border-radius:10px;background:linear-gradient(135deg,#4f8ef7,#6366f1);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;transition:opacity .14s;}
  .iw-chat-send-btn:disabled{opacity:.35;cursor:default;}
  .iw-chat-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;color:var(--text-3);font-size:.875rem;}
  .iw-chat-mob-toggle{width:32px;height:32px;border-radius:8px;border:1px solid var(--line-2);background:transparent;color:var(--text-2);cursor:pointer;display:none;align-items:center;justify-content:center;}
  @media(max-width:700px){.iw-chat-mob-toggle{display:flex;}}

  /* ── Quick reply templates ── */
  .iw-templates-bar {
    padding: 8px 14px 6px;
    border-top: 1px solid var(--line);
    background: var(--bg-1);
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .iw-templates-label {
    font-size: .6rem; font-weight: 800; letter-spacing: .08em;
    text-transform: uppercase; color: var(--text-3);
  }
  .iw-templates-list {
    display: flex; flex-wrap: wrap; gap: 6px;
  }
  .iw-tpl-btn {
    padding: 5px 11px; border-radius: 100px;
    background: rgba(124,58,237,.07); border: 1px solid rgba(124,58,237,.22);
    color: #a78bfa; font-family: var(--body); font-size: .72rem; font-weight: 600;
    cursor: pointer; transition: background .13s, border-color .13s;
    white-space: nowrap;
  }
  .iw-tpl-btn:hover { background: rgba(124,58,237,.16); border-color: rgba(124,58,237,.4); }

  .iw-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:8px;font-family:var(--body);font-size:.82rem;font-weight:600;cursor:pointer;border:none;transition:all .14s;white-space:nowrap;}
  .iw-btn-primary{background:linear-gradient(135deg,var(--blue),#6366f1);color:#fff;}
  .iw-btn-primary:hover{opacity:.88;}
  .iw-btn-ghost{background:transparent;border:1px solid var(--line-2);color:var(--text-2);}
  .iw-btn-ghost:hover{background:var(--surface);color:var(--text-1);}
  .iw-btn-purple{background:rgba(124,58,237,.12);border:1px solid rgba(124,58,237,.3);color:#a78bfa;}
  .iw-btn-purple:hover{background:rgba(124,58,237,.2);}
  .iw-btn:disabled{opacity:.4;cursor:not-allowed;}

  /* Interview sent state */
  .iw-invite-sent{
    display:inline-flex;align-items:center;gap:5px;
    padding:5px 11px;border-radius:8px;font-size:.75rem;font-weight:700;
    background:rgba(34,197,94,.12);border:1px solid rgba(34,197,94,.3);
    color:var(--green);cursor:default;white-space:nowrap;
  }

  /* Home button */
  .iw-home-btn{
    display:inline-flex;align-items:center;gap:6px;
    padding:6px 13px;border-radius:9px;
    background:rgba(79,142,247,.08);border:1px solid rgba(79,142,247,.2);
    color:var(--blue);font-size:.82rem;font-weight:600;
    cursor:pointer;font-family:var(--body);
    transition:background .13s,border-color .13s;
    white-space:nowrap;
  }
  .iw-home-btn:hover{background:rgba(79,142,247,.16);border-color:rgba(79,142,247,.4);}

  .iw-empty{display:flex;flex-direction:column;align-items:center;gap:10px;padding:3rem 1rem;color:var(--text-3);font-size:.875rem;text-align:center;}
  .iw-loading{display:flex;align-items:center;justify-content:center;gap:10px;padding:3rem;color:var(--text-3);}
  .iw-dash-spinner{width:20px;height:20px;border-radius:50%;border:2px solid var(--line-2);border-top-color:var(--blue);animation:iw-spin .7s linear infinite;}

  .iw-bottom-nav{display:none;position:fixed;bottom:0;left:0;right:0;z-index:30;background:var(--bg-1);border-top:1px solid var(--line);padding:6px 0 env(safe-area-inset-bottom);}
  @media(max-width:768px){.iw-bottom-nav{display:flex;}.iw-content{padding-bottom:80px;}}
  .iw-bottom-btn{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;padding:5px 0;background:none;border:none;cursor:pointer;color:var(--text-3);font-family:var(--body);font-size:.62rem;font-weight:600;transition:color .13s;position:relative;}
  .iw-bottom-btn.active{color:var(--blue);}
  .iw-bottom-badge{position:absolute;top:2px;right:calc(50% - 14px);min-width:14px;height:14px;border-radius:100px;padding:0 3px;background:#ef4444;color:#fff;font-size:.52rem;font-weight:800;display:flex;align-items:center;justify-content:center;}
`;

/* ── Icons ──────────────────────────────────────────────────────────────────── */
const HomeIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const CheckIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ── Toast hook ─────────────────────────────────────────────────────────────── */
function useNotify() {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const notify = useCallback((msg, type = "success") => {
    clearTimeout(timerRef.current);
    setToast({ msg, type });
    timerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  return [toast, notify];
}

/* ── Chat Panel ─────────────────────────────────────────────────────────────── */
function InterviewerChat() {
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const bottomRef = useRef(null);

  const fetchConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConvs(data);
    } catch {
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => {
    fetchConvs();
    const iv = setInterval(fetchConvs, 6000);
    return () => clearInterval(iv);
  }, [fetchConvs]);

  const openConv = useCallback(async (conv) => {
    setActiveConv(conv);
    setSidebarOpen(false);
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/chat/${conv.userId}`);
      setMessages(data);
    } catch {
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    if (!activeConv) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/chat/${activeConv.userId}`);
        setMessages(data);
      } catch {}
    }, 4000);
    return () => clearInterval(iv);
  }, [activeConv]);

  useEffect(() => {
    setTimeout(
      () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
      60,
    );
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending || !activeConv) return;
    setSending(true);
    setText("");
    try {
      const { data } = await api.post("/chat/send", {
        text: t,
        targetUserId: activeConv.userId,
      });
      setMessages((prev) => [...prev, data]);
    } catch {
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const grouped = messages.reduce((acc, msg) => {
    const d = fmtDateChat(msg.createdAt);
    if (!acc.length || acc[acc.length - 1].date !== d)
      acc.push({ date: d, msgs: [] });
    acc[acc.length - 1].msgs.push(msg);
    return acc;
  }, []);

  return (
    <div className="iw-chat-root">
      {sidebarOpen && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 19,
            background: "rgba(0,0,0,.45)",
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={`iw-chat-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="iw-chat-sb-hdr">
          <span className="iw-chat-sb-title">Conversations</span>
          {convs.reduce((s, c) => s + (c.unread || 0), 0) > 0 && (
            <span
              style={{
                background: "#ef4444",
                color: "#fff",
                borderRadius: 100,
                padding: "2px 7px",
                fontSize: ".65rem",
                fontWeight: 800,
              }}
            >
              {convs.reduce((s, c) => s + (c.unread || 0), 0)}
            </span>
          )}
        </div>
        <div className="iw-chat-conv-list">
          {loadingConvs ? (
            <div className="iw-loading" style={{ padding: "2rem" }}>
              <div className="iw-dash-spinner" />
            </div>
          ) : convs.length === 0 ? (
            <div className="iw-empty" style={{ padding: "2rem 1rem" }}>
              <p>No conversations yet.</p>
            </div>
          ) : (
            convs.map((c) => (
              <div
                key={c.conversationId}
                className={`iw-chat-conv-item${activeConv?.conversationId === c.conversationId ? " active" : ""}`}
                onClick={() => openConv(c)}
              >
                <div className="iw-chat-conv-av">
                  {initials(c.userName)}
                  {c.unread > 0 && (
                    <div className="iw-chat-conv-badge">{c.unread}</div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="iw-chat-conv-name">{c.userName}</div>
                  <div className="iw-chat-conv-preview">{c.lastMessage}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="iw-chat-area">
        {!activeConv ? (
          <div className="iw-chat-empty">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p>Select a conversation to start messaging</p>
          </div>
        ) : (
          <>
            <div className="iw-chat-hdr">
              <button
                className="iw-chat-mob-toggle"
                onClick={() => setSidebarOpen(true)}
              >
                <Icon name="menu" size={16} />
              </button>
              <div className="iw-chat-hdr-av">
                {initials(activeConv.userName)}
              </div>
              <div>
                <div className="iw-chat-hdr-name">{activeConv.userName}</div>
                <div className="iw-chat-hdr-email">{activeConv.userEmail}</div>
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: ".7rem",
                  color: "var(--green)",
                  fontWeight: 600,
                }}
              >
                <svg width="7" height="7" viewBox="0 0 8 8" fill="#22c55e">
                  <circle cx="4" cy="4" r="4" />
                </svg>
                Active
              </div>
            </div>

            <div className="iw-chat-msgs">
              {loadingMsgs && messages.length === 0 ? (
                <div className="iw-loading">
                  <div className="iw-dash-spinner" />
                </div>
              ) : messages.length === 0 ? (
                <div className="iw-chat-empty">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <p>No messages yet. Send the first one!</p>
                </div>
              ) : (
                grouped.map((g) => (
                  <div
                    key={g.date}
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <div className="iw-chat-date-sep">{g.date}</div>
                    {g.msgs.map((msg) => {
                      const isMe = msg.senderRole === "admin";
                      return (
                        <div
                          key={msg._id}
                          className={`iw-chat-msg ${isMe ? "me" : "them"}`}
                        >
                          {!isMe && (
                            <div className="iw-chat-sender">
                              {activeConv.userName}
                            </div>
                          )}
                          <div className="iw-chat-bubble">{msg.text}</div>
                          <div className="iw-chat-time">
                            {fmtTime(msg.createdAt)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
              <div ref={bottomRef} />
            </div>

            {/* ── Quick-reply templates ── */}
            <div className="iw-templates-bar">
              <div className="iw-templates-label">Quick replies</div>
              <div className="iw-templates-list">
                {INTERVIEW_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.label}
                    className="iw-tpl-btn"
                    title={tpl.text}
                    onClick={() => setText(tpl.text)}
                  >
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="iw-chat-input-row">
              <textarea
                className="iw-chat-textarea"
                placeholder={`Reply to ${activeConv.userName}…`}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height =
                    Math.min(e.target.scrollHeight, 120) + "px";
                }}
                onKeyDown={handleKey}
                rows={1}
                disabled={sending}
              />
              <button
                className="iw-chat-send-btn"
                onClick={send}
                disabled={!text.trim() || sending}
              >
                {sending ? (
                  <div className="iw-spinner" />
                ) : (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ══ Main InterviewerDashboard ══════════════════════════════════════════════ */
export default function InterviewerDashboard({ user, logout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [interviewSending, setInterviewSending] = useState(null);
  // Track which app IDs have had invites successfully sent
  const [interviewSent, setInterviewSent] = useState(new Set());
  const [statusChanging, setStatusChanging] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, notify] = useNotify();

  useEffect(() => {
    const id = "iw-dashboard-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.innerHTML = STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications");
      setApplications(data);
    } catch {
      notify("Failed to load applications.", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  useEffect(() => {
    const poll = () =>
      api
        .get("/chat/conversations")
        .then(({ data }) =>
          setUnreadCount(data.reduce((s, c) => s + (c.unread || 0), 0)),
        )
        .catch(() => {});
    poll();
    const iv = setInterval(poll, 8000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!sidebarOpen) return;
    const close = (e) => {
      if (
        !e.target.closest(".iw-sidebar") &&
        !e.target.closest(".iw-topbar-toggle")
      )
        setSidebarOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [sidebarOpen]);

  const navigateTab = (newTab) => {
    setSidebarOpen(false);
    setTab(newTab);
  };

  const changeStatus = useCallback(
    async (appId, status) => {
      setStatusChanging(appId);
      try {
        const { data } = await api.patch(`/applications/${appId}/status`, {
          status,
        });
        setApplications((prev) =>
          prev.map((a) => (a._id === data._id ? data : a)),
        );
        notify(`Marked as ${status}.`);
      } catch (err) {
        notify(
          err.response?.data?.message || "Failed to update status.",
          "error",
        );
      } finally {
        setStatusChanging(null);
      }
    },
    [notify],
  );

  const sendInterview = useCallback(
    async (app) => {
      setInterviewSending(app._id);
      try {
        await api.post(`/applications/${app._id}/interview`);
        // Mark as sent
        setInterviewSent((prev) => new Set([...prev, app._id]));
        notify(`Interview invitation sent to ${app.userName}!`);
      } catch (err) {
        notify(
          err.response?.data?.message || "Failed to send interview invite.",
          "error",
        );
      } finally {
        setInterviewSending(null);
      }
    },
    [notify],
  );

  const visibleApps = applications.filter((a) => a.status !== "accepted");
  const filteredApps = visibleApps.filter((a) => {
    const q = search.toLowerCase().trim();
    const ms =
      !q ||
      (a.userName || "").toLowerCase().includes(q) ||
      (a.userEmail || "").toLowerCase().includes(q) ||
      (a.jobTitle || "").toLowerCase().includes(q);
    const mst = statusFilter === "all" || a.status === statusFilter;
    return ms && mst;
  });

  const pendingCount = applications.filter(
    (a) => a.status === "pending",
  ).length;
  const reviewedCount = applications.filter(
    (a) => a.status === "reviewed",
  ).length;
  const rejectedCount = applications.filter(
    (a) => a.status === "rejected",
  ).length;
  const totalVisible = visibleApps.length;

  const navItems = [
    { id: "overview", icon: "home", label: "Overview", badge: 0 },
    {
      id: "applications",
      icon: "eye",
      label: "Applications",
      badge: pendingCount,
    },
    { id: "chat", icon: "chat", label: "Messages", badge: unreadCount },
  ];

  const filterPills = [
    { value: "all", label: `All (${totalVisible})` },
    { value: "pending", label: `Pending (${pendingCount})` },
    { value: "reviewed", label: `Reviewed (${reviewedCount})` },
    { value: "rejected", label: `Rejected (${rejectedCount})` },
  ];

  /* ── Interview button renderer ───────────────────────────────────────────── */
  const renderInterviewBtn = (a, mobile = false) => {
    if (a.status !== "pending" && a.status !== "reviewed") return null;
    if (interviewSent.has(a._id)) {
      return (
        <span className="iw-invite-sent">
          <CheckIcon /> Sent
        </span>
      );
    }
    return (
      <button
        className="iw-btn iw-btn-purple"
        style={{
          padding: mobile ? "4px 9px" : "5px 11px",
          fontSize: mobile ? ".72rem" : ".75rem",
        }}
        disabled={interviewSending === a._id}
        onClick={() => sendInterview(a)}
      >
        {interviewSending === a._id ? (
          <div className="iw-spinner" />
        ) : (
          "Send Invite"
        )}
      </button>
    );
  };

  return (
    <div className="iw-dash">
      {toast && <div className={`iw-toast ${toast.type}`}>{toast.msg}</div>}

      {sidebarOpen && (
        <div
          className="iw-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`iw-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="iw-sidebar-top">
          <img
            src="/logo.png"
            alt="Quantum Logics"
            width="24"
            height="24"
            style={{ borderRadius: 5, objectFit: "contain" }}
          />
          <span className="iw-sidebar-brand">Quantum Logics</span>
        </div>
        <div className="iw-sidebar-role-badge">
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
          Interviewer
        </div>
        <div className="iw-sidebar-label">Navigation</div>
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`iw-sidebar-btn${tab === item.id ? " active" : ""}`}
            onClick={() => navigateTab(item.id)}
          >
            <Icon name={item.icon} size={15} />
            {item.label}
            {item.badge > 0 && (
              <span className="iw-sidebar-badge">{item.badge}</span>
            )}
          </button>
        ))}
        <div className="iw-sidebar-footer">
          {/* Home button in sidebar */}
          <button
            className="iw-home-btn"
            style={{
              width: "100%",
              justifyContent: "center",
              marginBottom: ".625rem",
            }}
            onClick={() => navigate("/")}
          >
            <HomeIcon /> Go to Home
          </button>
          <div className="iw-sidebar-user-name">{user.name}</div>
          <div className="iw-sidebar-user-email">{user.email}</div>
          <button className="iw-sidebar-logout" onClick={logout}>
            <Icon name="logout" size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="iw-main">
        {/* Topbar */}
        <div className="iw-topbar">
          <button
            className="iw-topbar-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
          >
            <Icon name="menu" size={17} />
          </button>
          <span className="iw-topbar-title">
            {navItems.find((n) => n.id === tab)?.label ?? "Dashboard"}
          </span>
          <div className="iw-topbar-right">
            {/* Home button in topbar */}
            <button className="iw-home-btn" onClick={() => navigate("/")}>
              <HomeIcon /> Home
            </button>
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 100,
                fontSize: ".72rem",
                fontWeight: 700,
                background: "rgba(124,58,237,.12)",
                border: "1px solid rgba(124,58,237,.3)",
                color: "#a78bfa",
              }}
            >
              Interviewer
            </span>
            <button
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--text-2)",
                display: "flex",
                alignItems: "center",
              }}
              onClick={logout}
              title="Sign out"
            >
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>

        {/* Page content */}
        <div className="iw-content">
          {/* ═══ OVERVIEW ═══ */}
          {tab === "overview" && (
            <>
              <div className="iw-page-header">
                <h1>Welcome back, {user.name.split(" ")[0]} 👋</h1>
                <p>
                  You're logged in as an Interviewer. You can view and triage
                  applications and message applicants as the Quantum Team.
                </p>
              </div>

              <div className="iw-stats">
                <div className="iw-stat-card">
                  <div className="iw-stat-label">Pending</div>
                  <div
                    className="iw-stat-num"
                    style={{ color: "var(--amber)" }}
                  >
                    {pendingCount}
                  </div>
                  <div className="iw-stat-sub">awaiting review</div>
                </div>
                <div className="iw-stat-card">
                  <div className="iw-stat-label">Reviewed</div>
                  <div className="iw-stat-num" style={{ color: "var(--blue)" }}>
                    {reviewedCount}
                  </div>
                  <div className="iw-stat-sub">in progress</div>
                </div>
                <div className="iw-stat-card">
                  <div className="iw-stat-label">Rejected</div>
                  <div className="iw-stat-num" style={{ color: "var(--red)" }}>
                    {rejectedCount}
                  </div>
                  <div className="iw-stat-sub">not proceeding</div>
                </div>
                <div className="iw-stat-card">
                  <div className="iw-stat-label">Unread Messages</div>
                  <div
                    className="iw-stat-num"
                    style={{
                      color: unreadCount > 0 ? "#ef4444" : "var(--text-1)",
                    }}
                  >
                    {unreadCount}
                  </div>
                  <div className="iw-stat-sub">from applicants</div>
                </div>
              </div>

              <div
                className="iw-card"
                style={{
                  display: "flex",
                  gap: ".875rem",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      color: "var(--text-1)",
                      marginBottom: 4,
                      fontSize: ".9rem",
                    }}
                  >
                    Quick Actions
                  </div>
                  <div style={{ fontSize: ".8rem", color: "var(--text-3)" }}>
                    Jump straight into your work.
                  </div>
                </div>
                <button
                  className="iw-btn iw-btn-ghost"
                  onClick={() => navigateTab("applications")}
                >
                  <Icon name="eye" size={14} /> Review Applications
                </button>
                <button
                  className="iw-btn iw-btn-purple"
                  onClick={() => navigateTab("chat")}
                >
                  <Icon name="chat" size={14} /> Open Messages
                  {unreadCount > 0 && (
                    <span
                      style={{
                        background: "#ef4444",
                        borderRadius: 100,
                        padding: "0 6px",
                        fontSize: ".65rem",
                        color: "#fff",
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </button>
              </div>

              {visibleApps.length > 0 && (
                <div
                  className="iw-card"
                  style={{ padding: 0, overflow: "hidden" }}
                >
                  <div
                    style={{
                      padding: "1rem 1.25rem",
                      borderBottom: "1px solid var(--line)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: "var(--text-1)",
                        fontSize: ".9rem",
                      }}
                    >
                      Recent Applications
                    </span>
                    <button
                      className="iw-btn iw-btn-ghost"
                      style={{ padding: "4px 10px", fontSize: ".75rem" }}
                      onClick={() => navigateTab("applications")}
                    >
                      View All
                    </button>
                  </div>
                  <div className="iw-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Applicant</th>
                          <th>Position</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleApps.slice(0, 5).map((a) => (
                          <tr key={a._id}>
                            <td>
                              <div className="iw-td-user">
                                <div className="iw-td-avatar">
                                  {initials(a.userName)}
                                </div>
                                <div>
                                  <div className="iw-td-name">{a.userName}</div>
                                  <div className="iw-td-email">
                                    {a.userEmail}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="iw-td-secondary">{a.jobTitle}</td>
                            <td className="iw-td-secondary">
                              {fmtDate(a.appliedAt)}
                            </td>
                            <td>
                              <span
                                className={`badge ${STATUS_CONFIG[a.status]?.cls || "badge-gray"}`}
                              >
                                {STATUS_CONFIG[a.status]?.label}
                              </span>
                            </td>
                            <td>{renderInterviewBtn(a)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ═══ APPLICATIONS ═══ */}
          {tab === "applications" && (
            <>
              <div className="iw-page-header">
                <h1>Applications</h1>
                <p>
                  Review applicants, update their status, and send interview
                  invitations as the Quantum Team.
                </p>
              </div>

              {loading ? (
                <div className="iw-loading">
                  <div className="iw-dash-spinner" />
                  <span>Loading…</span>
                </div>
              ) : (
                <div
                  className="iw-card"
                  style={{ padding: 0, overflow: "hidden" }}
                >
                  <div
                    className="iw-filter-bar"
                    style={{ margin: "1rem 1rem 0" }}
                  >
                    <div className="iw-search-wrap">
                      <svg
                        className="iw-search-icon"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        className="iw-search"
                        placeholder="Search by name, email or position…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: ".35rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {filterPills.map((p) => (
                        <button
                          key={p.value}
                          className={`iw-pill${statusFilter === p.value ? (p.value === "all" ? " active" : ` active-${p.value}`) : ""}`}
                          onClick={() => setStatusFilter(p.value)}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <button
                      className="iw-btn iw-btn-ghost"
                      style={{ padding: "5px 10px", fontSize: ".75rem" }}
                      onClick={fetchApps}
                    >
                      <Icon name="refresh" size={12} /> Refresh
                    </button>
                  </div>

                  {filteredApps.length === 0 ? (
                    <div className="iw-empty">
                      <Icon name="briefcase" size={32} color="var(--text-3)" />
                      <p>
                        {visibleApps.length === 0
                          ? "No applications yet."
                          : "No applications match your filters."}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div
                        className="iw-table-wrap"
                        style={{ marginTop: "1rem" }}
                      >
                        <table>
                          <thead>
                            <tr>
                              <th>Applicant</th>
                              <th>Position</th>
                              <th>Experience</th>
                              <th>Applied</th>
                              <th>Status</th>
                              <th>Update Status</th>
                              <th>Interview</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredApps.map((a) => (
                              <tr key={a._id}>
                                <td>
                                  <div className="iw-td-user">
                                    <div className="iw-td-avatar">
                                      {initials(a.userName || a.userEmail)}
                                    </div>
                                    <div>
                                      <div className="iw-td-name">
                                        {a.userName}
                                      </div>
                                      <div className="iw-td-email">
                                        {a.userEmail}
                                      </div>
                                      {a.phone && (
                                        <div className="iw-td-email">
                                          {a.phone}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="iw-td-secondary">
                                  {a.jobTitle}
                                </td>
                                <td className="iw-td-secondary">
                                  {a.experience || "—"}
                                </td>
                                <td className="iw-td-secondary">
                                  {fmtDate(a.appliedAt)}
                                </td>
                                <td>
                                  <span
                                    className={`badge ${STATUS_CONFIG[a.status]?.cls || "badge-gray"}`}
                                  >
                                    {STATUS_CONFIG[a.status]?.label}
                                  </span>
                                </td>
                                <td>
                                  <select
                                    className="iw-status-select"
                                    value={a.status}
                                    disabled={statusChanging === a._id}
                                    onChange={(e) =>
                                      changeStatus(a._id, e.target.value)
                                    }
                                  >
                                    {INTERVIEWER_STATUSES.map((s) => (
                                      <option key={s} value={s}>
                                        {STATUS_CONFIG[s].label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  {renderInterviewBtn(a)}
                                  {a.status !== "pending" &&
                                    a.status !== "reviewed" &&
                                    !interviewSent.has(a._id) && (
                                      <span
                                        style={{
                                          fontSize: ".75rem",
                                          color: "var(--text-3)",
                                        }}
                                      >
                                        —
                                      </span>
                                    )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div
                        className="iw-card-list"
                        style={{ padding: ".75rem" }}
                      >
                        {filteredApps.map((a) => (
                          <div key={a._id} className="iw-list-item">
                            <div className="iw-list-avatar">
                              {initials(a.userName || a.userEmail)}
                            </div>
                            <div className="iw-list-info">
                              <div className="iw-list-name">{a.userName}</div>
                              <div className="iw-list-sub">
                                {a.jobTitle}
                                {a.experience ? ` · ${a.experience}` : ""}
                              </div>
                            </div>
                            <div className="iw-list-right">
                              <select
                                className="iw-status-select"
                                value={a.status}
                                disabled={statusChanging === a._id}
                                onChange={(e) =>
                                  changeStatus(a._id, e.target.value)
                                }
                                style={{ fontSize: ".72rem" }}
                              >
                                {INTERVIEWER_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {STATUS_CONFIG[s].label}
                                  </option>
                                ))}
                              </select>
                              {renderInterviewBtn(a, true)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ═══ CHAT ═══ */}
          {tab === "chat" && (
            <div
              style={{ margin: "-24px -28px", height: "calc(100vh - 57px)" }}
            >
              <InterviewerChat />
            </div>
          )}
        </div>

        {/* Bottom nav (mobile) */}
        <nav className="iw-bottom-nav">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`iw-bottom-btn${tab === item.id ? " active" : ""}`}
              onClick={() => navigateTab(item.id)}
            >
              <span style={{ position: "relative", display: "inline-flex" }}>
                <Icon name={item.icon} size={19} />
                {item.badge > 0 && (
                  <span className="iw-bottom-badge">{item.badge}</span>
                )}
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
